/**
 * `POST /internal/cron/revenue-hourly-fold`.
 *
 * Folds UTC hours of the revenue "live arc" into the hour rollups of migrations 0121 and 0122:
 *   - `revenue_funding_hourly`: the funding attribution (`usageFundingCtesSql`) per (hour, brand), read by
 *     /summary and /summary/by-brand for the part of the window the funding day rollup does not serve;
 *   - `revenue_ledger_hourly`: the by-model/daily measures of the `revenue_ledger` usage rows, read by
 *     /by-model/daily for the days at or after the shadow line (Shanghai today - 14 days);
 *   - `revenue_summary_hourly` (0122): the ledger arm of /summary and /summary/by-brand (topup columns, usage upstream
 *     cost as unrounded micros, L2 profit, the float L2 margin denominator) per (hour, account, brand), read for the
 *     sealed hours of the live arc; a window that starts before the line keeps its older part on the statement the
 *     reader always had.
 * Control-loops ticks it every 10 minutes, and only while `REVENUE_HOURLY_FOLD_ARMED` is "1": unarmed,
 * nothing is requested, read or written. The readers use the tables only under `REVENUE_HOURLY_READ`
 * (`off` default | `compare` | `on`); `compare` answers from the live statements and logs every difference
 * against the hour tables (`revenue_hourly_compare`, `revenue_hourly_compare_diff`).
 *
 * An hour is sealed when it ended {@link REVENUE_HOURLY_SEAL_GRACE_MS} ago, the rating outbox holds no
 * unrated event enqueued before its end, and no credit-draw charge of the hour is waiting for its
 * allocation. A late change (a new ledger row or a new allocation of a sealed hour) is found from the
 * time-ordered ids (`rvl_t<ms>`, `bca_t<ms>`: an id-range tail scan, no new index) and un-seals the hour
 * (the readers go back to the live statement for it) until the next fold writes it again. A change the
 * tail scan cannot see (an UPDATE in place) is caught by the audit, which recomputes sealed hours
 * oldest-audited first and replaces a row set that differs (`revenue_hourly_audit_diff`).
 *
 * STALENESS (the contract of the readers): a served hour lags the live statements by at most one fold
 * interval plus the scan margin for a change the tail scan sees, and by the audit cycle for one it cannot.
 * A reader ignores the tables altogether when the fold has not completed a tick in the last
 * {@link REVENUE_HOURLY_READ_MAX_AGE_MS} (1 hour), so a paused or disarmed fold sends the readers back to
 * the live statements. The hours still unsealed (the last ~3) are always read live.
 *
 * Every hour runs under the platform's usual statement budget (the same 8 s as the funding rollup); an
 * hour that times out is parked in the process for a backoff and does not hold up the hours behind it.
 * cron_run_history is written by control-loops' tick-level recordCronRun.
 */

/** An hour is folded once its end is this old (and the data conditions above hold). */
export const REVENUE_HOURLY_SEAL_GRACE_MS = 2 * 60 * 60 * 1000;
/** A reader ignores the hour tables when the fold's state row is older than this. */
export const REVENUE_HOURLY_READ_MAX_AGE_MS = 60 * 60 * 1000;
/** The tail scans re-read this much before the last scan point (a row committed late keeps its id's time). */
export const REVENUE_HOURLY_SCAN_MARGIN_MS = 15 * 60 * 1000;
/** The fold covers the shadow line minus this many days, up to now. */
export const REVENUE_HOURLY_BACKFILL_LEAD_DAYS = 2;

/** Hours one call may fold (late-changed hours first, then missing hours newest first, then the audit). */
export const REVENUE_HOURLY_FOLD_MAX_HOURS_PER_CALL = 6;
export const REVENUE_HOURLY_FOLD_MAX_HOURS_CEILING = 24;
/** Sealed hours recomputed and compared per call: never audited first (newest first), then the least recently audited. */
export const REVENUE_HOURLY_FOLD_AUDIT_HOURS_PER_CALL = 3;
export const REVENUE_HOURLY_FOLD_AUDIT_HOURS_CEILING = 8;
/** The same statement budget and wall as `revenue_funding_materialize`; no preset raises them. */
export const REVENUE_HOURLY_FOLD_STATEMENT_TIMEOUT_MS = 8_000;
export const REVENUE_HOURLY_FOLD_STATEMENT_TIMEOUT_MIN_MS = 2_000;
export const REVENUE_HOURLY_FOLD_STATEMENT_TIMEOUT_MAX_MS = REVENUE_HOURLY_FOLD_STATEMENT_TIMEOUT_MS;
export const REVENUE_HOURLY_FOLD_WALL_MS = 25_000;
export const REVENUE_HOURLY_FOLD_WALL_MIN_MS = 10_000;
export const REVENUE_HOURLY_FOLD_WALL_MAX_MS = REVENUE_HOURLY_FOLD_WALL_MS;

export type RevenueHourlyReadMode = 'off' | 'compare' | 'on';

export interface RevenueHourlyFoldCronRequest {
  readonly maxHours?: number;
  readonly auditHours?: number;
  /** Per-statement budget; at most the default. */
  readonly statementTimeoutMs?: number;
  /** Route wall budget; at most the default. */
  readonly wallMs?: number;
}

export interface RevenueHourlyFoldCronResponse {
  readonly ok: boolean;
  /** Set when the platform is not armed: nothing was read or written. */
  readonly skipped?: 'off';
  /** Hours written for the first time (or after an un-seal) this call. */
  readonly foldedHours: number;
  /** Sealed hours the audit recomputed. */
  readonly auditedHours: number;
  /** Audited hours whose stored rows differed from the live statements (replaced). */
  readonly auditDiffHours: number;
  /** Sealed hours un-sealed this call because a ledger row or an allocation changed after the seal. */
  readonly dirtyHours: number;
  /** Hours in range still without rows after this call (not parked, not waiting on a seal condition). */
  readonly remaining_hours: number;
  /** Hours this call lost to statement_timeout (SQLSTATE 57014); they are parked. */
  readonly timeout_hours: number;
  /** Hours waiting out a backoff after an earlier timeout. */
  readonly parked_hours: number;
  /** Hours past the grace that the outbox / pending-charge conditions keep open. */
  readonly open_hours: number;
  readonly errors: readonly string[];
}
